import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { FileUpload } from 'primereact/fileupload';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import axios from "axios";

import { useAuth } from "./AuthContext";
import { useTerminologia } from "../hooks/useTerminologia";

const STAT_THEMES = {
    blue:   { bg: '#eff8ff', color: '#1570ef' },
    green:  { bg: '#ecfdf3', color: '#12b76a' },
    purple: { bg: '#f4ebff', color: '#7f56d9' },
};

const StatCard = ({ icon, label, value, theme }) => {
    const t = STAT_THEMES[theme];
    return (
        <div style={{
            background: '#fff', border: '1px solid #eaecf0', borderRadius: 14,
            padding: '1.1rem 1.3rem', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flex: '1 1 220px',
        }}>
            <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', color: '#98a2b3', textTransform: 'uppercase' }}>
                    {label}
                </div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#101828', marginTop: '0.2rem' }}>
                    {value}
                </div>
            </div>
            <div style={{
                width: 44, height: 44, borderRadius: 12, background: t.bg, color: t.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
            }}>
                <i className={icon} />
            </div>
        </div>
    );
};

const TableBarrios2 = () => {

    const { administracionId } = useAuth();
    const t = useTerminologia();

    const toast = useRef();
    const menuRef = useRef(null);
    const [menuBarrio, setMenuBarrio] = useState(null);

    const HOST_API = process.env.REACT_APP_URL;
    const url = `${HOST_API}api/barrios/GetBarriosConResumen?administracionId=${administracionId}`;

    const [barrios, setBarrios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tiposLiquidacion, setTiposLiquidacion] = useState([]);
    const [busqueda, setBusqueda] = useState('');

    const fetchBarrios = async () => {
        try {
            const response = await axios.get(url);
            setBarrios(response.data);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: `No se pudieron obtener los ${t.barrios}`, life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const fetchTiposLiquidacion = async () => {
        try {
            const { data } = await axios.get(`${HOST_API}api/tiposLiquidacion/GetTiposLiquidacion`);
            setTiposLiquidacion(data || []);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron obtener los tipos de liquidación', life: 3000 });
        }
    };

    useEffect(() => {
        fetchBarrios();
        fetchTiposLiquidacion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialEditFormData = {
        barrioId: 0,
        tipoLiquidacionId: 0,
        nombreBarrio: '',
        direccion: '',
        cuit: '',
        email: '',
        activo: true,
        administracionId: administracionId,
        razonSocial: '',
        numeroCuenta: '',
        cuentaBancaria: '',
        tipoCuentaBancaria: '',
        alias: '',
        imagenUrl: ''
    };

    const [editVisible, setEditVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editFormData, setEditFormData] = useState(initialEditFormData);
    const [imagenArchivo, setImagenArchivo] = useState(null);

    const handleNewClick = () => {
        setEditFormData({ ...initialEditFormData, administracionId });
        setImagenArchivo(null);
        setEditVisible(true);
    };

    const handleEditClick = async (rowData) => {
        setEditLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/barrios/getBarrios?barrioId=${rowData.barrioId}&administracionId=${administracionId}`);
            const barrio = Array.isArray(data) ? data[0] : data;
            if (!barrio) {
                throw new Error(`${t.Barrio} no encontrado`);
            }
            setEditFormData({
                barrioId: barrio.barrioId,
                tipoLiquidacionId: barrio.tipoLiquidacionId,
                nombreBarrio: barrio.nombreBarrio || '',
                direccion: barrio.direccion || '',
                cuit: barrio.cuit || '',
                email: barrio.email || '',
                activo: barrio.activo,
                administracionId: barrio.administracionId,
                razonSocial: barrio.razonSocial || '',
                numeroCuenta: barrio.numeroCuenta || '',
                cuentaBancaria: barrio.cuentaBancaria || '',
                tipoCuentaBancaria: barrio.tipoCuentaBancaria || '',
                alias: barrio.alias || '',
                imagenUrl: rowData.imagenUrl || ''
            });
            setImagenArchivo(null);
            setEditVisible(true);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: `No se pudo obtener el ${t.Barrio.toLowerCase()}`, life: 3000 });
        } finally {
            setEditLoading(false);
        }
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        const esNuevo = !editFormData.barrioId;
        setEditSaving(true);
        try {
            const { data: barrioId } = await axios.post(`${HOST_API}api/barrios/SaveBarrio`, editFormData);

            if (imagenArchivo && barrioId) {
                const fd = new FormData();
                fd.append('file', imagenArchivo);
                await axios.post(
                    `${HOST_API}api/barrios/SubirImagenBarrio?barrioId=${barrioId}&administracionId=${administracionId}`,
                    fd
                );
            }

            setEditVisible(false);
            setImagenArchivo(null);
            showSuccess(t.Barrios, `${t.Barrio} ${editFormData.nombreBarrio} ${esNuevo ? 'creado' : 'actualizado'} correctamente.`);
            await fetchBarrios();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: `No se pudo guardar el ${t.Barrio.toLowerCase()}`, life: 3000 });
        } finally {
            setEditSaving(false);
        }
    };

    const showSuccess = (titulo, mensaje) => {
        toast.current.show({ severity: 'success', summary: titulo, detail: mensaje, life: 3000 });
    };

    // ─── Estadísticas del encabezado ───────────────────────────────────────────
    const totalBarrios = barrios.length;
    const totalUF = useMemo(() => barrios.reduce((sum, b) => sum + (b.cantidadUF || 0), 0), [barrios]);
    const barriosActivos = useMemo(() => barrios.filter(b => b.activo).length, [barrios]);

    // ─── Búsqueda ───────────────────────────────────────────────────────────────
    const barriosFiltrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return barrios;
        return barrios.filter(b =>
            (b.nombreBarrio || '').toLowerCase().includes(q) ||
            (b.razonSocial || '').toLowerCase().includes(q) ||
            (b.cuit || '').toLowerCase().includes(q)
        );
    }, [barrios, busqueda]);

    // ─── Templates de columnas ──────────────────────────────────────────────────
    const entidadBody = (row) => (
        <div className="flex align-items-center gap-3">
            <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: row.activo ? '#f4ebff' : '#f2f4f7',
                color: row.activo ? '#7f56d9' : '#98a2b3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.05rem', flexShrink: 0,
            }}>
                <i className="pi pi-building" />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#101828', fontSize: '0.92rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.nombreBarrio}
                </div>
                {(row.razonSocial || row.direccion) && (
                    <div style={{ fontSize: '0.76rem', color: '#98a2b3', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.razonSocial || row.direccion}
                    </div>
                )}
            </div>
        </div>
    );

    const portfolioBody = (row) => (
        <div className="flex align-items-center gap-2" style={{ color: '#475467', fontSize: '0.88rem' }}>
            <i className="pi pi-home" style={{ color: '#98a2b3' }} />
            {row.cantidadUF || 0} {row.cantidadUF === 1 ? t.UnidadFuncional : t.UnidadesFuncionales}
        </div>
    );

    const contactoBody = (row) => (
        <div style={{ fontSize: '0.82rem', color: '#475467' }}>
            {row.email && (
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-envelope" style={{ color: '#98a2b3', fontSize: '0.78rem' }} />
                    <span>{row.email}</span>
                </div>
            )}
            {row.direccion && (
                <div className="flex align-items-center gap-2" style={{ marginTop: row.email ? 2 : 0 }}>
                    <i className="pi pi-map-marker" style={{ color: '#98a2b3', fontSize: '0.78rem' }} />
                    <span>{row.direccion}</span>
                </div>
            )}
            {!row.email && !row.direccion && '—'}
        </div>
    );

    const estadoBody = (row) => {
        const s = row.activo
            ? { bg: '#ecfdf3', color: '#12b76a', label: 'Activo' }
            : { bg: '#fef3f2', color: '#f04438', label: 'No Activo' };
        return (
            <span style={{
                background: s.bg, color: s.color,
                fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.03em',
                padding: '0.25rem 0.65rem', borderRadius: 99, textTransform: 'uppercase',
            }}>
                {s.label}
            </span>
        );
    };

    const accionesBody = (row) => (
        <Button
            icon="pi pi-ellipsis-v"
            rounded text
            loading={editLoading && menuBarrio?.barrioId === row.barrioId}
            onClick={(e) => { setMenuBarrio(row); menuRef.current.toggle(e); }}
            aria-label="Acciones"
        />
    );

    const menuItems = [
        { label: 'Editar', icon: 'pi pi-pencil', command: () => menuBarrio && handleEditClick(menuBarrio) },
    ];

    return (
        <>
            <h4>{t.Barrios}</h4>
            <br />

            <Toast ref={toast} />
            <Menu model={menuItems} popup ref={menuRef} />

            <Dialog
                header={editFormData.barrioId ? `Editar ${t.Barrio}` : `Nuevo ${t.Barrio}`}
                visible={editVisible}
                style={{ width: '520px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setEditVisible(false)}
            >
                <form onSubmit={handleSaveEdit}>
                    <div className="flex flex-column gap-3 mt-2">
                        <div>
                            <label style={labelStyle}>Nombre <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText value={editFormData.nombreBarrio} onChange={(e) => setEditFormData(p => ({ ...p, nombreBarrio: e.target.value }))} className="p-inputtext-sm w-full" required />
                        </div>
                        <div>
                            <label style={labelStyle}>Dirección</label>
                            <InputText value={editFormData.direccion} onChange={(e) => setEditFormData(p => ({ ...p, direccion: e.target.value }))} className="p-inputtext-sm w-full" />
                        </div>
                        <div>
                            <label style={labelStyle}>Tipo de Liquidación <span style={{ color: '#f04438' }}>*</span></label>
                            <Dropdown
                                value={editFormData.tipoLiquidacionId}
                                options={tiposLiquidacion}
                                optionLabel="descripcion"
                                optionValue="tipoLiquidacionId"
                                onChange={(e) => setEditFormData(p => ({ ...p, tipoLiquidacionId: e.value }))}
                                placeholder="Seleccione un tipo de liquidación"
                                className="p-inputtext-sm w-full"
                                required
                            />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label style={labelStyle}>CUIT</label>
                                <InputText value={editFormData.cuit} onChange={(e) => setEditFormData(p => ({ ...p, cuit: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                            <div className="flex-1">
                                <label style={labelStyle}>Email</label>
                                <InputText value={editFormData.email} onChange={(e) => setEditFormData(p => ({ ...p, email: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Razón Social</label>
                            <InputText value={editFormData.razonSocial} onChange={(e) => setEditFormData(p => ({ ...p, razonSocial: e.target.value }))} className="p-inputtext-sm w-full" />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label style={labelStyle}>Número de Cuenta</label>
                                <InputText value={editFormData.numeroCuenta} onChange={(e) => setEditFormData(p => ({ ...p, numeroCuenta: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                            <div className="flex-1">
                                <label style={labelStyle}>Cuenta Bancaria</label>
                                <InputText value={editFormData.cuentaBancaria} onChange={(e) => setEditFormData(p => ({ ...p, cuentaBancaria: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label style={labelStyle}>Tipo de Cuenta Bancaria</label>
                                <InputText value={editFormData.tipoCuentaBancaria} onChange={(e) => setEditFormData(p => ({ ...p, tipoCuentaBancaria: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                            <div className="flex-1">
                                <label style={labelStyle}>Alias</label>
                                <InputText value={editFormData.alias} onChange={(e) => setEditFormData(p => ({ ...p, alias: e.target.value }))} className="p-inputtext-sm w-full" />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Imagen</label>
                            {editFormData.imagenUrl && !imagenArchivo && (
                                <img
                                    src={`${HOST_API}${editFormData.imagenUrl}`.replace(/([^:])\/\/+/g, '$1/')}
                                    alt={editFormData.nombreBarrio}
                                    style={{ maxHeight: 120, borderRadius: 8, display: 'block', marginBottom: 8 }}
                                />
                            )}
                            <FileUpload
                                mode="basic"
                                name="imagen"
                                accept=".jpg,.jpeg,.png"
                                maxFileSize={5000000}
                                chooseLabel={editFormData.imagenUrl ? 'Cambiar imagen' : 'Adjuntar imagen'}
                                onSelect={(e) => setImagenArchivo(e.files[0])}
                                onClear={() => setImagenArchivo(null)}
                            />
                        </div>
                        <div className="flex align-items-center gap-2">
                            <InputSwitch checked={!!editFormData.activo} onChange={(e) => setEditFormData(p => ({ ...p, activo: e.value }))} />
                            <label style={labelStyle}>Activo</label>
                        </div>
                        <div className="flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setEditVisible(false)} />
                            <Button label="Guardar" icon="pi pi-save" type="submit" loading={editSaving} />
                        </div>
                    </div>
                </form>
            </Dialog>

            <div className="flex align-items-center gap-3 mb-3">
                <Button label={`Nuevo ${t.Barrio}`} icon="pi pi-plus" onClick={handleNewClick} />
            </div>

            {/* ─── Cards de resumen ───────────────────────────────────────────── */}
            <div className="flex gap-3 flex-wrap mb-3">
                <StatCard icon="pi pi-map" label={`Total ${t.Barrios}`} value={totalBarrios} theme="blue" />
                <StatCard icon="pi pi-home" label={`Total ${t.UnidadesFuncionales}`} value={totalUF} theme="green" />
                <StatCard icon="pi pi-check-circle" label={`${t.Barrios} Activos`} value={barriosActivos} theme="purple" />
            </div>

            {/* ─── Card con búsqueda + listado ────────────────────────────────── */}
            <div style={{ background: '#fff', border: '1px solid #eaecf0', borderRadius: 14, overflow: 'hidden' }}>
                <div className="flex align-items-center justify-content-between gap-3" style={{ padding: '0.9rem 1.1rem', borderBottom: '1px solid #f2f4f7' }}>
                    <span className="p-input-icon-left" style={{ flex: 1, maxWidth: 380 }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder={`Buscar por nombre, razón social o CUIT...`}
                            className="p-inputtext-sm w-full"
                        />
                    </span>
                    {busqueda && (
                        <Button label="Reset" text className="p-button-sm" onClick={() => setBusqueda('')} />
                    )}
                </div>

                <DataTable
                    value={barriosFiltrados}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    dataKey="barrioId"
                    loading={loading}
                    responsiveLayout="scroll"
                    emptyMessage={`No se encontraron ${t.barrios}.`}
                    className="p-datatable-sm"
                    currentPageReportTemplate={`Mostrando {first} a {last} de {totalRecords} ${t.barrios}`}
                    paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
                >
                    <Column header={<span style={theadStyle}>{t.Barrio} / Entidad</span>} body={entidadBody} style={{ minWidth: '16rem' }} />
                    <Column header={<span style={theadStyle}>Portfolio</span>} body={portfolioBody} style={{ minWidth: '10rem' }} />
                    <Column header={<span style={theadStyle}>Contacto</span>} body={contactoBody} style={{ minWidth: '14rem' }} />
                    <Column header={<span style={theadStyle}>Estado</span>} body={estadoBody} style={{ minWidth: '8rem' }} />
                    <Column header={<span style={theadStyle}>Acciones</span>} body={accionesBody} style={{ width: '5rem', textAlign: 'center' }} />
                </DataTable>
            </div>
        </>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const theadStyle = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', color: '#98a2b3', textTransform: 'uppercase' };

export default TableBarrios2;
